import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Image,
  Modal,
  Row,
  Space,
  Spin,
  Steps,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  LinkOutlined,
  PhoneOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "../../services/axiosInstance";
import { FETCH_SIGNED_DOCUMENTS, GET_TRIP } from "../../services/apiEndPoints";
import { ITrip, ITripDetailResponse, ITripUploadEntry, TripStatus } from "../types/trip.types";
import { getDocTypeConfig, isPdfUrl } from "../rake/docTypeLabels";

const { Text, Title } = Typography;

const STATUS_STEPS: TripStatus[] = [
  "pending",
  "tare_weighed",
  "loading",
  "gross_weighed",
  "dispatched",
  "delivered",
  "completed",
];

const STATUS_LABELS: Record<TripStatus, string> = {
  pending: "Pending",
  tare_weighed: "Tare Weighed",
  loading: "Loading",
  gross_weighed: "Gross Weighed",
  dispatched: "Dispatched",
  delivered: "Delivered",
  completed: "Completed",
};

const UPLOAD_SLOTS: { key: keyof ITrip["uploads"]; label: string }[] = [
  { key: "vehicle_gatepass", label: "Vehicle Gate Pass" },
  { key: "weightment_slip", label: "Weightment Slip" },
  { key: "final_weightment", label: "Final Weightment" },
  { key: "surveyor_seal", label: "Surveyor Seal" },
  { key: "driver_arrival", label: "Driver Arrival" },
  { key: "challan_slip", label: "Challan Slip" },
  { key: "truck_sheet", label: "Truck Sheet" },
  { key: "form_515", label: "Form 515" },
  { key: "wharfage_demurrage", label: "Wharfage / Demurrage" },
];

interface TripDetailDrawerProps {
  open: boolean;
  tripId: string | null;
  onClose: () => void;
}

const TripDetailDrawer = ({ open, tripId, onClose }: TripDetailDrawerProps) => {
  const [loading, setLoading] = useState(false);
  const [trip, setTrip] = useState<ITrip | null>(null);
  const [rakeInfo, setRakeInfo] = useState<ITripDetailResponse["data"]["rake"] | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLabel, setPreviewLabel] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchTrip = useCallback(async () => {
    if (!tripId) return;
    setLoading(true);
    try {
      const res = await axios.get(GET_TRIP(tripId));
      const d = res?.data?.data;
      setTrip(d?.trip ?? null);
      setRakeInfo(d?.rake ?? null);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        message.error("Trip not found");
      } else {
        message.error("Failed to load trip details");
      }
      setTrip(null);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    if (open && tripId) {
      fetchTrip();
    } else {
      setTrip(null);
      setRakeInfo(null);
    }
  }, [open, tripId, fetchTrip]);

  const openPreview = useCallback(async (entry: ITripUploadEntry, label: string) => {
    setPreviewLabel(label);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const res = await axios.get(
        `${FETCH_SIGNED_DOCUMENTS}?s3Url=${encodeURIComponent(entry.s3Url)}`
      );
      const signed = res?.data?.results || res?.data?.data || entry.s3Url;
      setPreviewUrl(signed);
    } catch {
      setPreviewUrl(entry.s3Url);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const currentStepIndex = trip ? STATUS_STEPS.indexOf(trip.status) : 0;

  const uploadedCount = trip
    ? UPLOAD_SLOTS.filter((s) => !!trip.uploads?.[s.key]).length
    : 0;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width="min(760px, 96vw)"
        title={
          trip ? (
            <Space size={10} align="center">
              <Text strong style={{ fontSize: 15 }}>
                {trip.vehicleNumber}
              </Text>
              <Tag
                color={
                  trip.status === "completed"
                    ? "success"
                    : trip.status === "delivered"
                    ? "green"
                    : trip.status === "pending"
                    ? "default"
                    : "blue"
                }
                style={{ borderRadius: 6 }}
              >
                {STATUS_LABELS[trip.status] || trip.status}
              </Tag>
              <Badge
                count={`${uploadedCount}/9`}
                style={{
                  backgroundColor:
                    uploadedCount === 9 ? "#16a34a" : "#1677ff",
                }}
              />
            </Space>
          ) : (
            "Trip details"
          )
        }
        destroyOnClose
        styles={{ body: { padding: "16px 20px" } }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin size="large" />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">Loading trip…</Text>
            </div>
          </div>
        ) : !trip ? (
          <Empty description="No trip data" style={{ padding: 48 }} />
        ) : (
          <Space direction="vertical" style={{ width: "100%" }} size={20}>
            {/* Status steps */}
            <div
              style={{
                background: "#f8fafc",
                borderRadius: 10,
                padding: "14px 16px",
                border: "1px solid #e2e8f0",
              }}
            >
              <Title level={5} style={{ margin: "0 0 14px", fontSize: 13 }}>
                Status Progress
              </Title>
              <Steps
                current={currentStepIndex}
                size="small"
                items={STATUS_STEPS.map((s, i) => ({
                  title: (
                    <span style={{ fontSize: 11 }}>{STATUS_LABELS[s]}</span>
                  ),
                  status:
                    i < currentStepIndex
                      ? "finish"
                      : i === currentStepIndex
                      ? trip.status === "completed"
                        ? "finish"
                        : "process"
                      : "wait",
                }))}
              />
            </div>

            {/* Trip + rake info */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Title level={5} style={{ margin: "0 0 10px", fontSize: 13 }}>
                  Trip Info
                </Title>
                <Descriptions
                  column={1}
                  size="small"
                  styles={{ label: { fontSize: 12, color: "#64748b" }, content: { fontSize: 12 } }}
                >
                  <Descriptions.Item label="Wagon No">
                    <Text style={{ fontFamily: "monospace" }}>
                      {trip.wagonNo || "—"}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="DC Number">
                    {trip.dcNumber || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Destination">
                    {trip.unloadingDestination || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Unloading Date">
                    {trip.unloadingDate
                      ? dayjs(trip.unloadingDate).format("MMM D, YYYY")
                      : "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="TC No">
                    {trip.tcNo || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cisco Seal 1">
                    {trip.ciscoSealNo1 || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cisco Seal 2">
                    {trip.ciscoSealNo2 || "—"}
                  </Descriptions.Item>
                </Descriptions>
              </Col>

              <Col xs={24} md={12}>
                <Title level={5} style={{ margin: "0 0 10px", fontSize: 13 }}>
                  Driver
                </Title>
                <Descriptions
                  column={1}
                  size="small"
                  styles={{ label: { fontSize: 12, color: "#64748b" }, content: { fontSize: 12 } }}
                >
                  <Descriptions.Item
                    label={
                      <Space size={4}>
                        <UserOutlined />
                        Name
                      </Space>
                    }
                  >
                    {trip.driverName || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={
                      <Space size={4}>
                        <PhoneOutlined />
                        Phone
                      </Space>
                    }
                  >
                    {trip.driverPhone || "—"}
                  </Descriptions.Item>
                </Descriptions>

                <Title level={5} style={{ margin: "14px 0 10px", fontSize: 13 }}>
                  Weights
                </Title>
                <Descriptions
                  column={1}
                  size="small"
                  styles={{ label: { fontSize: 12, color: "#64748b" }, content: { fontSize: 12 } }}
                >
                  <Descriptions.Item label="Tare">
                    {trip.tareWeight
                      ? `${trip.tareWeight.toLocaleString()} kg`
                      : "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Gross">
                    {trip.grossWeight
                      ? `${trip.grossWeight.toLocaleString()} kg`
                      : "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Net">
                    {trip.netWeight ? (
                      <Text strong style={{ color: "#0f766e" }}>
                        {trip.netWeight.toLocaleString()} kg
                      </Text>
                    ) : (
                      "—"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Bags delivered">
                    {trip.bagsDelivered ?? "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Bags at goodshed">
                    {trip.bagsAtGoodshed ?? "—"}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>

            {rakeInfo && (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: 12,
                  color: "#64748b",
                }}
              >
                <Space wrap size={16}>
                  <span>
                    <Text type="secondary">Rake: </Text>
                    <Text strong style={{ fontSize: 12 }}>
                      {rakeInfo.rakeMasterId}
                    </Text>
                  </span>
                  {rakeInfo.rrNumber && (
                    <span>
                      <Text type="secondary">RR: </Text>
                      <Text style={{ fontSize: 12 }}>{rakeInfo.rrNumber}</Text>
                    </span>
                  )}
                  {rakeInfo.company && (
                    <span>
                      <Text type="secondary">Company: </Text>
                      <Text style={{ fontSize: 12 }}>{rakeInfo.company}</Text>
                    </span>
                  )}
                  <Tag
                    color={rakeInfo.isActive ? "green" : "default"}
                    style={{ borderRadius: 6, fontSize: 11 }}
                  >
                    {rakeInfo.isActive ? "Active" : "Closed"}
                  </Tag>
                </Space>
              </div>
            )}

            {/* Documents */}
            <div>
              <Space
                style={{ marginBottom: 12, width: "100%", justifyContent: "space-between" }}
              >
                <Title level={5} style={{ margin: 0, fontSize: 13 }}>
                  Documents ({uploadedCount} / 9)
                </Title>
              </Space>
              <Row gutter={[10, 10]}>
                {UPLOAD_SLOTS.map(({ key, label }) => {
                  const entry = trip.uploads?.[key];
                  const cfg = getDocTypeConfig(key);
                  const pdf = entry ? isPdfUrl(entry.s3Url) : false;
                  return (
                    <Col xs={12} sm={8} key={key}>
                      {entry ? (
                        <div
                          onClick={() => openPreview(entry, label)}
                          style={{
                            cursor: "pointer",
                            borderRadius: 8,
                            overflow: "hidden",
                            border: "1px solid #e2e8f0",
                            background: "#fff",
                            transition: "border-color 0.15s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.borderColor = "#1677ff")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.borderColor = "#e2e8f0")
                          }
                        >
                          <div
                            style={{
                              height: 90,
                              background: "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                            }}
                          >
                            {!pdf ? (
                              <img
                                src={entry.s3Url}
                                alt={label}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  const img = e.target as HTMLImageElement;
                                  img.style.display = "none";
                                  const parent =
                                    img.parentElement as HTMLElement | null;
                                  if (parent)
                                    parent.innerHTML =
                                      '<span style="color:#cbd5e1;font-size:28px">📄</span>';
                                }}
                              />
                            ) : (
                              <FileTextOutlined
                                style={{ fontSize: 28, color: "#94a3b8" }}
                              />
                            )}
                            <div
                              style={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                background: "rgba(15,23,42,0.65)",
                                borderRadius: 4,
                                padding: "1px 5px",
                                color: "#fff",
                                fontSize: 10,
                              }}
                            >
                              View
                            </div>
                          </div>
                          <div style={{ padding: "6px 8px" }}>
                            <Tag
                              color={cfg.color}
                              style={{ margin: 0, borderRadius: 5, fontSize: 10 }}
                            >
                              {label}
                            </Tag>
                            {entry.serialNo != null && (
                              <Text
                                type="secondary"
                                style={{ display: "block", fontSize: 10, marginTop: 2 }}
                              >
                                #{entry.serialNo}
                              </Text>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            borderRadius: 8,
                            border: "1px dashed #e2e8f0",
                            background: "#fafafa",
                            padding: "12px 8px",
                            textAlign: "center",
                            minHeight: 120,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                          }}
                        >
                          <ClockCircleOutlined
                            style={{ fontSize: 20, color: "#cbd5e1" }}
                          />
                          <Text
                            type="secondary"
                            style={{ fontSize: 10, textAlign: "center" }}
                          >
                            {label}
                          </Text>
                          <Tag
                            style={{
                              borderRadius: 5,
                              fontSize: 10,
                              margin: 0,
                              background: "#f1f5f9",
                              border: "none",
                              color: "#94a3b8",
                            }}
                          >
                            Pending
                          </Tag>
                        </div>
                      )}
                    </Col>
                  );
                })}
              </Row>
            </div>

            <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "right" }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              Updated {dayjs(trip.updatedAt).fromNow()}
            </div>
          </Space>
        )}
      </Drawer>

      {/* Document preview modal */}
      <Modal
        open={previewUrl !== null || previewLoading}
        onCancel={() => {
          setPreviewUrl(null);
          setPreviewLabel("");
        }}
        footer={null}
        width="80vw"
        centered
        destroyOnClose
        zIndex={1100}
        title={
          <Space size={8}>
            <Text strong>{previewLabel}</Text>
            {previewUrl && (
              <Tooltip title="Open in new tab">
                <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                  <Button size="small" icon={<LinkOutlined />} />
                </a>
              </Tooltip>
            )}
          </Space>
        }
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
            background: "#0f172a",
            borderRadius: 8,
          }}
        >
          {previewLoading || !previewUrl ? (
            <Spin />
          ) : isPdfUrl(previewUrl) ? (
            <iframe
              src={previewUrl}
              title="preview"
              style={{
                width: "100%",
                height: "70vh",
                border: "none",
                borderRadius: 8,
                background: "#fff",
              }}
            />
          ) : (
            <Image
              src={previewUrl}
              alt="preview"
              style={{ maxHeight: "70vh", objectFit: "contain" }}
              preview={false}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default TripDetailDrawer;
